import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { RetryPaymentModal } from '../components/RetryPaymentModal';
import { getOrder } from '../api/client';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import { Package, ArrowLeft, Clock, Check, Truck, MapPin, MessageCircle, ExternalLink, CreditCard, AlertCircle, RefreshCw, X, ShoppingBag, Info, Play, ChevronDown, ChevronUp } from 'lucide-react';
import type { Order, OrderStatus, ItemSaleWithPayment, PaymentStatus, Shipment, ShipmentStatus } from '../types';
import { UserAvatar } from '../components/UserAvatar';
import { Img } from '../components/Img';
import type { LucideIcon } from 'lucide-react';

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  OPEN: { label: 'Open', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  UNSHIPPED: { label: 'Awaiting Shipment', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Package },
  PARTIALLY_SHIPPED: { label: 'Partially Shipped', color: '#F97316', bgColor: '#FFF7ED', Icon: Package },
  LABEL_CREATED: { label: 'Label Created', color: '#8B5CF6', bgColor: '#EDE9FE', Icon: Package },
  SHIPPED: { label: 'Shipped', color: '#3B82F6', bgColor: '#DBEAFE', Icon: Truck },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
};

const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  PENDING: { label: 'Pending', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  AUTHORIZED: { label: 'Payment Authorized', color: '#3B82F6', bgColor: '#DBEAFE', Icon: CreditCard },
  CAPTURED: { label: 'Paid', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  FAILED: { label: 'Payment Failed', color: '#DC2626', bgColor: '#FEE2E2', Icon: AlertCircle },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
  REFUNDED: { label: 'Refunded', color: '#8B5CF6', bgColor: '#EDE9FE', Icon: RefreshCw },
  PARTIALLY_REFUNDED: { label: 'Partial Refund', color: '#8B5CF6', bgColor: '#EDE9FE', Icon: RefreshCw },
  VOIDED: { label: 'Voided', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
};

const ITEM_SHIPPING_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  unshipped: { label: 'Unshipped', color: '#F59E0B', bgColor: '#FEF3C7' },
  created: { label: 'Pending', color: '#F59E0B', bgColor: '#FEF3C7' },
  label_purchased: { label: 'Label Created', color: '#8B5CF6', bgColor: '#EDE9FE' },
  in_transit: { label: 'In Transit', color: '#3B82F6', bgColor: '#DBEAFE' },
  delivered: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7' },
  exception: { label: 'Exception', color: '#EF4444', bgColor: '#FEE2E2' },
};

const SHIPMENT_STATUS_CONFIG: Record<ShipmentStatus, { label: string; color: string; bgColor: string }> = {
  CREATED: { label: 'Created', color: '#F59E0B', bgColor: '#FEF3C7' },
  LABEL_PURCHASED: { label: 'Label Created', color: '#8B5CF6', bgColor: '#EDE9FE' },
  IN_TRANSIT: { label: 'In Transit', color: '#3B82F6', bgColor: '#DBEAFE' },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7' },
  EXCEPTION: { label: 'Exception', color: '#EF4444', bgColor: '#FEE2E2' },
};

const CARRIER_LOGOS: Record<string, string> = {
  USPS: '/img/logos/carrier-usps.svg',
  UPS: '/img/logos/carrier-ups.svg',
  FedEx: '/img/logos/carrier-fx.svg',
};

function getTrackingUrl(carrier: string, trackingNumber: string): string | null {
  switch (carrier) {
    case 'USPS': return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    case 'UPS': return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    case 'FedEx': return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    default: return null;
  }
}

function formatDate(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrySale, setRetrySale] = useState<{ id: string; itemName: string; amount: number } | null>(null);
  const [expandedChargeId, setExpandedChargeId] = useState<string | null>(null);
  useDocumentTitle(`Order #${order?.id ?? ''}`);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setIsLoading(true);
      const data = await getOrder(orderId!);
      setOrder(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loading}>Loading order...</div>
        </div>
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.error}>{error || 'Order not found'}</div>
        </div>
      </Layout>
    );
  }

  const statusConfig = STATUS_CONFIG[order.status];
  const shipments = order.shipments || [];
  const sales = order.itemSales || [];

  // Compute payment summary from item sales
  const subtotal = sales.reduce((sum, s) => sum + s.winningBid, 0);
  const shipping = sales.reduce((sum, s) => sum + s.shippingCost - (s.shippingDiscount || 0), 0);
  const tax = sales.reduce((sum, s) => sum + s.tax, 0);
  const total = sales.reduce((sum, s) => sum + s.total, 0);
  const totalBalanceApplied = sales.reduce((sum, s) => sum + ((s as any).balanceApplied || 0), 0);
  const cardTotal = total - totalBalanceApplied;

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button onClick={() => navigate('/orders')} style={styles.backButton}>
            <ArrowLeft size={20} />
            <span>Back to Orders</span>
          </button>

          <div style={styles.header}>
            <h1 style={styles.title}>Order Details</h1>
            <div
              style={{
                ...styles.statusBadge,
                backgroundColor: statusConfig.bgColor,
                color: statusConfig.color,
              }}
            >
              <statusConfig.Icon size={16} />
              <span>{statusConfig.label}</span>
            </div>
          </div>

          {/* Show + Order info */}
          <div style={styles.showInfoRow}>
            {order.showThumbnailUrl ? (
              <Img src={order.showThumbnailUrl} alt={order.showName ?? 'Show'} variant="thumb" renderedWidth={48} style={styles.showThumbnail} />
            ) : (
              <div style={styles.showThumbnailPlaceholder}>
                <Package size={20} color={colors.gray[300]} />
              </div>
            )}
            <div style={styles.showMeta}>
              <span style={styles.showNameText}>{order.showName ?? 'Show'}</span>
              <span style={styles.orderMetaText}>Order number: {order.id}</span>
              <span style={styles.orderMetaText}>Order Date: {formatDate(order.createdAt)}</span>
              <span style={styles.orderMetaText}>Order Total: {formatCurrency(total)}</span>
            </div>
          </div>

          {/* Seller tile */}
          {order.sellerUsername && (
            <div style={styles.sellerSection}>
              <span style={styles.sellerLabel}>Seller</span>
              <div style={styles.sellerTile}>
                <div
                  style={styles.sellerTileLink}
                  onClick={() => navigate(`/user/${order.sellerUsername}`)}
                >
                  <UserAvatar src={order.sellerAvatarUrl} alt={order.sellerUsername} size={32} />
                  <span style={styles.sellerName}>{order.sellerUsername}</span>
                </div>
                <button
                  onClick={() => {
                    const params = new URLSearchParams({
                      recipientId: order.sellerId,
                      orderId: order.id,
                    });
                    if (order.showName) {
                      params.set('itemName', order.showName);
                    }
                    navigate(`/inbox?${params.toString()}`);
                  }}
                  style={styles.messageSellerButtonSmall}
                >
                  <MessageCircle size={14} />
                  <span>Message</span>
                </button>
              </div>
            </div>
          )}

          {/* OPEN banner */}
          {order.status === 'OPEN' && (
            <div style={styles.openBanner}>
              <div style={styles.openBannerContent}>
                <Info size={16} color="#3B82F6" style={{ flexShrink: 0 }} />
                <span>This show is still in progress. Order will be processed when the show ends.</span>
              </div>
              <button
                onClick={() => navigate(`/watch/${order.showId}`)}
                style={styles.joinShowButton}
              >
                <Play size={14} />
                <span>Join Show</span>
              </button>
            </div>
          )}

          {/* Items Card */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <ShoppingBag size={16} />
              Items ({sales.length})
            </h2>
            <div style={styles.itemsList}>
              {sales.map((sale: ItemSaleWithPayment) => {
                const paymentConfig = PAYMENT_STATUS_CONFIG[sale.paymentStatus || 'PENDING'];
                const shippingStatusConfig = ITEM_SHIPPING_STATUS_CONFIG[sale.shippingStatus || 'unshipped'];
                const isCancelled = sale.paymentStatus === 'CANCELLED' || sale.paymentStatus === 'VOIDED';
                return (
                  <div key={sale.id} style={{ ...styles.itemRow, ...(isCancelled ? { opacity: 0.5 } : {}) }}>
                    <div style={styles.itemInfo}>
                      {sale.itemSnapshot?.imageUrl ? (
                        <Img
                          src={sale.itemSnapshot.imageUrl}
                          alt={sale.itemSnapshot.name}
                          variant="thumb"
                          renderedWidth={48}
                          blurhash={sale.itemSnapshot.blurhash}
                          style={styles.itemImage}
                        />
                      ) : (
                        <div style={styles.itemImagePlaceholder}>
                          <Package size={20} color={colors.gray[300]} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={styles.itemName}>{sale.itemSnapshot?.name ?? 'Unknown Item'}</p>
                        <div style={styles.badgeRow}>
                          <span style={{
                            ...styles.itemBadge,
                            backgroundColor: paymentConfig.bgColor,
                            color: paymentConfig.color,
                          }}>
                            {paymentConfig.label}
                          </span>
                          <span style={{
                            ...styles.itemBadge,
                            backgroundColor: shippingStatusConfig.bgColor,
                            color: shippingStatusConfig.color,
                          }}>
                            {shippingStatusConfig.label}
                          </span>
                        </div>
                        {sale.paymentStatus === 'FAILED' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRetrySale({
                                id: sale.id,
                                itemName: sale.itemSnapshot?.name ?? 'Unknown Item',
                                amount: sale.total,
                              });
                            }}
                            style={styles.retryButton}
                          >
                            Retry Payment
                          </button>
                        )}
                      </div>
                    </div>
                    <span style={styles.itemPrice}>{formatCurrency(sale.winningBid)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Shipping Address Card */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <MapPin size={16} />
              Shipping Address
            </h2>
            <div style={styles.addressBlock}>
              <p style={styles.addressName}>{order.recipientName}</p>
              <p style={styles.addressLine}>{order.streetLine1}</p>
              {order.streetLine2 && <p style={styles.addressLine}>{order.streetLine2}</p>}
              <p style={styles.addressLine}>{order.city}, {order.state} {order.zipCode}</p>
            </div>
          </div>

          {/* Shipment Tracking Cards */}
          {shipments.length > 0 && shipments.map((shipment: Shipment) => {
            const shipStatusConfig = SHIPMENT_STATUS_CONFIG[shipment.status];
            const carrierLogo = shipment.carrier ? CARRIER_LOGOS[shipment.carrier] : null;
            const trackingUrl = shipment.carrier && shipment.trackingNumber
              ? getTrackingUrl(shipment.carrier, shipment.trackingNumber)
              : null;
            return (
              <div key={shipment.id} style={styles.card}>
                <div style={styles.shipmentHeader}>
                  <h2 style={styles.sectionTitle}>
                    <Truck size={16} />
                    Shipment
                  </h2>
                  <span style={{
                    ...styles.itemBadge,
                    backgroundColor: shipStatusConfig.bgColor,
                    color: shipStatusConfig.color,
                  }}>
                    {shipStatusConfig.label}
                  </span>
                </div>
                {shipment.trackingNumber && (
                  <div style={styles.trackingSection}>
                    {carrierLogo && (
                      <img src={carrierLogo} alt={shipment.carrier ?? ''} style={styles.carrierLogo} />
                    )}
                    <div style={styles.trackingInfo}>
                      <span style={styles.trackingLabel}>Tracking Number</span>
                      <span style={styles.trackingNumber}>{shipment.trackingNumber}</span>
                    </div>
                    {trackingUrl && (
                      <a href={trackingUrl} target="_blank" rel="noopener noreferrer" style={styles.trackPackageLink}>
                        <span>Track</span>
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                )}
                {shipment.shippedAt && (
                  <p style={styles.shippingDate}>Shipped on {formatDate(shipment.shippedAt)}</p>
                )}
                {shipment.deliveredAt && (
                  <p style={styles.shippingDate}>Delivered on {formatDate(shipment.deliveredAt)}</p>
                )}
              </div>
            );
          })}

          {/* Payment Summary Card */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Payment Summary</h2>
            <div style={styles.summaryRow}>
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Shipping</span>
              <span>{formatCurrency(shipping)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Tax</span>
              <span>{formatCurrency(tax)}</span>
            </div>
            <div style={styles.divider} />
            {totalBalanceApplied > 0 && (
              <div style={styles.summaryRow}>
                <span style={{ color: '#5F3DC4' }}>Snagg Balance</span>
                <span style={{ color: '#16A34A' }}>-{formatCurrency(totalBalanceApplied)}</span>
              </div>
            )}
            <div style={styles.totalRow}>
              <span>Total</span>
              <span>{formatCurrency(totalBalanceApplied > 0 ? cardTotal : total)}</span>
            </div>
          </div>

          {/* Charges Card */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <CreditCard size={16} />
              Charges ({sales.length})
            </h2>
            {sales.map((sale: ItemSaleWithPayment) => {
              const saleBalance = (sale as any).balanceApplied || 0;
              const saleCard = (sale as any).cardAmount || 0;
              const isFullyBalance = saleBalance > 0 && saleCard === 0;
              const isSplit = saleBalance > 0 && saleCard > 0;
              const cardLabel = sale.cardBrand
                ? `${sale.cardBrand.charAt(0).toUpperCase() + sale.cardBrand.slice(1)} ••••${sale.cardLast4}`
                : null;
              const pmLabel = isFullyBalance
                ? 'Snagg Balance'
                : isSplit ? `Snagg Balance + ${cardLabel || 'Card'}` : cardLabel;
              const isExpanded = expandedChargeId === sale.id;
              return (
                <div
                  key={sale.id}
                  onClick={() => setExpandedChargeId(isExpanded ? null : sale.id)}
                  style={{ padding: '10px 0', borderBottom: `1px solid ${colors.gray[100]}`, cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                      <p style={{ fontSize: 13, fontWeight: 500, color: colors.ink.black, margin: 0 }}>
                        {sale.itemSnapshot?.name ?? 'Item'}
                      </p>
                      {pmLabel && (
                        <p style={{ fontSize: 11, color: isFullyBalance ? '#5F3DC4' : colors.gray[600], margin: '2px 0 0' }}>
                          {pmLabel}
                        </p>
                      )}
                    </div>
                    {sale.paymentStatus === 'FAILED' && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#EF4444', background: '#EF444420', padding: '2px 6px', borderRadius: 100, marginRight: 8 }}>
                        Failed
                      </span>
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600, color: colors.ink.black, marginRight: 6 }}>
                      {formatCurrency(sale.total)}
                    </span>
                    {isExpanded ? <ChevronUp size={14} color={colors.gray[400]} /> : <ChevronDown size={14} color={colors.gray[400]} />}
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${colors.gray[100]}`, fontSize: 12, color: colors.gray[600] }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span>Item</span><span>{formatCurrency(sale.winningBid)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span>Shipping</span><span>{formatCurrency(sale.shippingCost)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: saleBalance > 0 ? 4 : 0 }}>
                        <span>Tax</span><span>{formatCurrency(sale.tax)}</span>
                      </div>
                      {saleBalance > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: saleCard > 0 ? 4 : 0 }}>
                          <span style={{ color: '#5F3DC4' }}>Snagg Balance</span>
                          <span style={{ color: '#16A34A' }}>-{formatCurrency(saleBalance)}</span>
                        </div>
                      )}
                      {saleCard > 0 && saleBalance > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Charged to card</span><span>{formatCurrency(saleCard)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {retrySale && (
        <RetryPaymentModal
          saleId={retrySale.id}
          itemName={retrySale.itemName}
          amount={retrySale.amount}
          onClose={() => setRetrySale(null)}
          onSuccess={() => {
            setRetrySale(null);
            loadOrder();
          }}
        />
      )}
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '700px',
    margin: '0 auto',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.gray[600],
  },
  error: {
    textAlign: 'center',
    padding: '48px',
    color: colors.accent.pink,
    backgroundColor: '#FEE2E2',
    borderRadius: '12px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: 0,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 600,
    flexShrink: 0,
  },
  showInfoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
  },
  showThumbnail: {
    width: '56px',
    height: '56px',
    borderRadius: '10px',
    objectFit: 'cover' as const,
    flexShrink: 0,
  },
  showThumbnailPlaceholder: {
    width: '56px',
    height: '56px',
    borderRadius: '10px',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  showMeta: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  showNameText: {
    fontSize: '15px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '2px',
  },
  orderMetaText: {
    fontSize: '13px',
    color: colors.gray[600],
  },
  sellerSection: {
    marginBottom: '16px',
  },
  sellerLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    marginBottom: '6px',
  },
  sellerTile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
  },
  sellerTileLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
  },
  sellerAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
  },
  sellerAvatarPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: colors.gray[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 700,
    color: colors.gray[600],
  },
  sellerName: {
    fontSize: '14px',
    color: colors.ink.black,
    fontWeight: 600,
  },
  messageSellerButtonSmall: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue15,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  openBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#DBEAFE',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#1E40AF',
    marginBottom: '16px',
  },
  openBannerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
  },
  joinShowButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1E40AF',
    backgroundColor: '#BFDBFE',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '20px',
    border: `1px solid ${colors.gray[300]}`,
    marginBottom: '16px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 16px 0',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
  },
  itemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  itemImage: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
  },
  itemImagePlaceholder: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: colors.gray[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemName: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: '0 0 6px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  badgeRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap' as const,
  },
  itemBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '100px',
    fontSize: '11px',
    fontWeight: 600,
  },
  itemPrice: {
    fontSize: '15px',
    fontWeight: 700,
    color: colors.ink.black,
    flexShrink: 0,
  },
  retryButton: {
    marginTop: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  addressBlock: {
    lineHeight: 1.6,
  },
  addressName: {
    fontSize: '15px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: 0,
  },
  addressLine: {
    fontSize: '14px',
    color: colors.gray[700],
    margin: 0,
  },
  shipmentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  trackingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
    marginBottom: '12px',
  },
  carrierLogo: {
    width: '32px',
    height: '32px',
    objectFit: 'contain',
  },
  trackingInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  trackingLabel: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  trackingNumber: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  trackPackageLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.brand.blue,
    textDecoration: 'none',
  },
  shippingDate: {
    fontSize: '13px',
    color: colors.gray[600],
    margin: '0 0 4px 0',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: colors.gray[700],
    marginBottom: '12px',
  },
  divider: {
    height: '1px',
    backgroundColor: colors.gray[300],
    margin: '12px 0',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    fontWeight: 700,
    color: colors.ink.black,
  },
};
